import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import utc from 'dayjs/plugin/utc';
import { Box, Button as MuiButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { App, Button, Form, Spin } from 'antd';
import { AutoIcon } from 'assets/icons/common';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetQueryV3DeltaQuery, useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation, useIdentityBulkMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import EditIcon from 'assets/icons/edit';
import RestorePageOutlinedIcon from '@mui/icons-material/RestorePageOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import LeaseRecordManagementV9HistoryTable from './LeaseRecordManagementV9HistoryTable';
import LeaseRecordManagementV9ScheduleDetail from './LeaseRecordManagementV9ScheduleDetail';
import LeaseRecordManagementV9AgreementFiles from './LeaseRecordManagementV9AgreementFiles';
import LeaseManagementV9GeneralLedgersDetail from './LeaseManagementV9GeneralLedgersDetail';
import LeaseDetailBasicInformation from './LeaseDetailBasicInformation';
import LeaseRecordManagementV9AccountAssignmentDetail from './LeaseRecordManagementV9AccountAssignmentDetail';
import { constantFormValues } from 'static-solutions/24155/identity-solution/lease-v9/lease-record-management-v9/root';
import {
	hasGlKey,
	isSequential,
	stateUpdater,
	utcConverter,
	isGrossRentLessThanAdvanceOnEachItem,
} from 'static-solutions/24155/identity-solution/lease-v9/lease-record-management-v9/CustomComponents/LeaseUtils/helper';

const leaseDataCache = {};

dayjs.extend(utc);

const LeaseRecordManagementDetails = ({ screen = 'CREATE' }) => {
	const { message: messageApi } = App.useApp();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const location = useLocation();
	const { state, pathname } = location;
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();
	const pinStorageKey = `lease-record-management-v9-details:pinned:${pathname}`;
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');
	const createInitTimeoutRef = useRef(null);
	const backgroundFetchFrameRef = useRef(null);
	const isMountedRef = useRef(true);

	const cacheKey = screen?.toLowerCase() === 'view' ? `${state?._id}-${state?.processCode}` : null;

	const initialCachedData = cacheKey ? leaseDataCache[cacheKey] : null;

	const [tabInstanceId] = useState(() => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

	const [selectedTab, setSelectedTab] = useState(0);
	const [selectHistory, setSelectHistory] = useState(null);
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		showActiveDeactiveModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	const { lynFormProperties } = useControlData();

	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [creationApi, { isLoading: isCreatLoading, error: createError, isSuccess: isCreateSuccess, isError: isCreateError }] =
		useGetSingleIdentityMutation();

	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const displayLeaseData = cachedLeaseData || singleLeaseData;

	const { profileData } = useSelector((state) => state?.profile);

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'view'
			? !(
					(displayLeaseData?.activationStatus === 'W-I-P' &&
						displayLeaseData?.recordStatus === 'parked' &&
						profileData?.userId === displayLeaseData?.approverUserId[0]) ||
					(displayLeaseData?.activationStatus === 'W-I-P' && profileData?.userId === displayLeaseData?.creatorId)
				)
				? displayLeaseData?.data
				: displayLeaseData?.parkedData
			: [];
	}, [displayLeaseData, profileData?.userId, screen]);

	const hasGlAssigned = hasGlKey(statusData);

	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;

			if (createInitTimeoutRef.current) {
				clearTimeout(createInitTimeoutRef.current);
			}

			if (backgroundFetchFrameRef.current) {
				cancelAnimationFrame(backgroundFetchFrameRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (screen?.toLowerCase() === 'create') {
			form.resetFields();
			form.setFieldsValue(initialFormValues);
			setDataFullyLoaded(true);
			return undefined;
		}

		if (screen?.toLowerCase() === 'view') {
			if (!initialCachedData) {
				const fetchLeaseData = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						leaseDataCache[cacheKey] = response;

						if (isMountedRef.current) {
							setCachedLeaseData(response);
						}
					} catch (error) {
						console.error('Error fetching lease data:', error);
					}
				};
				fetchLeaseData();
			} else {
				const fetchInBackground = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						if (!isEqual(initialCachedData, response)) {
							leaseDataCache[cacheKey] = response;

							if (isMountedRef.current) {
								setCachedLeaseData(response);
							}
						}
					} catch (error) {
						// Silent fail for background fetch
					}
				};

				backgroundFetchFrameRef.current = requestAnimationFrame(() => {
					fetchInBackground();
				});
			}
		}
	}, [screen, initialCachedData, state?._id, state?.processCode, cacheKey]);

	useEffect(() => {
		if (screen === 'CREATE') {
			if (isCreateSuccess) {
				toast.success('Success! Action completed ', toastConfig);

				navigate(-1);
			}
			if (isCreateError) {
				if (createError?.data?.code === '501') {
					toast.error(createError?.data?.message, toastConfig);
				} else {
					toast.error('Failed! Action not completed', toastConfig);
				}
			}
		}
	}, [isCreateSuccess, createError, isCreateError, screen]);

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			leaseId: '',
			leaseTitle: '',
			leaseType: 'Finance Lease',
			lessorName: '',
			lessorID: null,
			leaseTerm: '',
			leaseEndDate: '',
			creationDate: dayjs(),
			lastModifiedDate: dayjs(),
			escalationPercentage: '0',
			inputChangeMonth: '1',
			grossRentList: [
				{
					from: 1,
					to: '',
					amount: '',
					remarks: '',
				},
			],
			grossRentListSet: [[]],
			advanceAmountListSet: [[]],

			advanceAmountList: [
				{
					from: 1,
					to: '',
					amount: '',
					totalAmount: '',
				},
			],
			advanceAmount: '0',
			advanceDuration: '0',
			upfrontLeasePayment: '',
			initialDirectCost: '',
			securityDeposit: '',
			initialDiscountRate: '0',
			// rightOfUseAssetValue: '10000250 - RoU Front Offices',
			// glleaseLiability: '20001120 - Lease Liabilities',
			// costCenterInterestExpense: '902001 - Finance Common',
			// glInterestExpense: '40004250 - Interest for Lease',
			// leasePayableControlAccount: '20001100 - Lease Payable',
			// glAdvanceRent: '10002050 - Advance Rent',
			// depreciationRoUCostCenter: '902003 - Finance Common',
			// depreciationRoU: '20007555 - Depreciation RoU',
			// accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
			// glBankAccount: '10003001 - SCB Main',
			// leaseExpense: '40004260 - Interest for Lease',
			// leaseExpenseCostCenter: '902002 - Finance Common',
			// securityDepositHead: '10009008 - Security Deposite',
			// accountsPayableHead: '20003300 - Accounts Payable',
			identityId: '',
			cashIncentiveAmount: '',

			paymentModality: 'Start',
			paymentFrequency: 'Monthly',
			endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
			changes: [
				{
					inputChangeEffectiveDate: '',
					inputChangeAdvance: '',
					inputChangeAdvanceAmounts: [],
					inputChangeDescription: 'Initial Rent',
					inputChangeDiscountRate: 0,
					inputChangeEscalation: 0,
					inputChangeGrossAmount: 0,
					inputChangeGrossRents: [],
					inputChangeLeaseTerm: '',
					paymentFrequency: 'Monthly',
					paymentTime: 'Start',
				},
			],
			agreementFiles: [
				{
					id: '',
					name: '',
					file: '',
				},
			],
		}),
		[]
	);

	useEffect(() => {
		if (screen?.toLowerCase() === 'view' && statusData) {
			const viewValues = {
				tabIndex: '1',
				leaseId: statusData?.leaseId,
				lessorID: statusData?.lessorID,
				leaseType: statusData?.leaseType,
				lessorName: statusData?.lessorName,
				leaseTitle: statusData?.leaseTitle,
				paymentModality: statusData?.paymentModality,
				paymentFrequency: statusData?.paymentFrequency,
				lastModifiedDate: dayjs(statusData?.lastModifiedDate),
				modificationSequence: statusData?.modificationSequence,
				creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,

				// Financial Terms & Conditions section
				advanceAmount: statusData?.advanceAmount,
				advanceDuration: statusData?.advanceDuration,
				grossRent: statusData?.grossRent,
				leaseTerm: statusData?.leaseTerm,
				leaseStartDate: dayjs(statusData?.leaseStartDate),
				leaseEndDate: dayjs(statusData?.leaseEndDate),
				escalationPercentage: statusData?.escalationPercentage,
				initialDiscountRate: statusData?.initialDiscountRate,
				upfrontLeasePayment: statusData?.upfrontLeasePayment ?? 0,
				initialDirectCost: statusData?.initialDirectCost ?? 0,
				securityDeposit: statusData?.securityDeposit ?? 0,
				endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
				// Accounting Information section
				rightOfUseAssetValue: statusData?.rightOfUseAssetValue,
				glleaseLiability: statusData?.glleaseLiability ?? '',
				costCenterInterestExpense: statusData?.costCenterInterestExpense,
				glInterestExpense: statusData?.glInterestExpense,
				leaseExpenseCostCenter: statusData?.leaseExpenseCostCenter,
				leaseExpense: statusData?.leaseExpense,
				leasePayableControlAccount: statusData?.leasePayableControlAccount,
				glAdvanceRent: statusData?.glAdvanceRent,
				depreciationRoUCostCenter: statusData?.depreciationRoUCostCenter,
				depreciationROU: statusData?.depreciationRoU,
				accumDepreciationROU: statusData?.accumDepreciationRoU,
				glBankAccount: statusData?.glBankAccount,
				securityDepositHead: statusData?.securityDepositHead,
				accountsPayableHead: statusData?.accountsPayableHead,
				effectiveDateHistory: statusData?.effectiveDateHistory,
				cashIncentiveAmount: statusData?.cashIncentiveAmount || statusData?.cashIncentive,
				//others
				version: String(displayLeaseData?.version),
				leaseLiability: statusData?.leaseLiability,
				amortizationSchedule: statusData?.amortizationSchedule,
				leaseCategory: statusData?.leaseCategory,
				leaseName: statusData?.leaseName,
				contactDetails: statusData?.contactDetails,
				identityId: displayLeaseData?._id,

				renewalOptions: statusData?.renewalOptions,
				terminationOption: statusData?.terminationOption,
				month: statusData?.month,
				refundability: statusData?.refundability,
				agreementFiles: statusData?.agreementFiles,

				responsibility: statusData?.responsibility,
				basisOfRate: statusData?.basisOfRate,
				nonLeaseComponents: statusData?.nonLeaseComponents,
				separateServiceCharges: statusData?.separateServiceCharges,
				modificationDetails: statusData?.modificationDetails,
				discountRateChanges: statusData?.discountRateChanges,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				grossRentList: statusData?.grossRentList,
				grossRentListSet: statusData?.grossRentListSet,
				advanceAmountListSet: statusData?.advanceAmountListSet,
				advanceAmountList: statusData?.advanceAmountList,

				//changes
				changes: statusData?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
					};
				}),
			};
			// console.log('ÃƒÂ°Ã…Â¸Ã…Â¡Ã¢â€šÂ¬ ~ changes:statusData?.changes?.map ~ statusData:', statusData);

			form.setFieldsValue(viewValues);
			setDataFullyLoaded(true);
		}
	}, [displayLeaseData?._id, form, screen, statusData]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();

			const formData = form.getFieldsValue();

			// console.log('ÃƒÂ°Ã…Â¸Ã…Â¡Ã¢â€šÂ¬ ~ handleSubmit ~ formData:', formData);

			if (formData?.paymentFrequency === 'Quarterly' && formData?.leaseTerm % 3 !== 0) {
				messageApi.error(
					"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
				);
				return;
			}
			if (!isSequential(formData?.grossRentList)) {
				messageApi.error('Gross Rent List must be sequential');
				return;
			}
			if (!isSequential(formData?.advanceAmountList)) {
				messageApi.error('Advance Amount List must be sequential');
				return;
			}
			let grossRentListSet = [];
			let advanceAmountListSet = [];
			let effectiveDateHistory = [];
			grossRentListSet?.push(formData?.grossRentList);
			// console.log(grossRentListSet, 'formData gr');

			advanceAmountListSet?.push(formData?.advanceAmountList);
			effectiveDateHistory?.push(utcConverter(formData?.leaseStartDate));

			const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents, formData?.advanceAmounts);
			if (!isGrossRentAndAdvanceAmountValid) {
				messageApi.error('Inconsistency between Lease Payment and Advance Adjustment');
				return;
			}
			const createPagePayload = {
				modificationSequence: 0,
				leaseStartDate: utcConverter(formData?.leaseStartDate),
				leaseTitle: formData?.leaseTitle,
				lessorName: formData?.lessorName,
				lessorID: formData?.lessorID,
				leaseTerm: formData?.leaseTerm,
				leaseEndDate: utcConverter(formData?.leaseEndDate),
				paymentModality: formData?.paymentModality,
				leaseType: formData?.leaseType,
				initialDiscountRate: formData?.initialDiscountRate,
				grossRentList: formData?.grossRentList,
				paymentFrequency: formData?.paymentFrequency,
				advanceAmountList: formData?.advanceAmountList,
				advanceAmount: formData?.advanceAmount,
				advanceAmounts: formData?.advanceAmounts,
				upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
				initialDirectCost: formData?.initialDirectCost ?? 0,
				securityDeposit: formData?.securityDeposit ?? 0,
				grossRents: formData?.grossRents,
				cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				grossRent: 0,
				escalationPercentage: 0,
				grossRentListSet,
				advanceAmountListSet,
				effectiveDateHistory,
				modifyCount: 0,
				editCount: 0,
				changes: [
					{
						paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
						paymentTime: formData?.changes?.[0]?.paymentTime,
						inputChangeLeaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
						inputChangeGrossAmount: formData?.changes?.[0]?.inputChangeGrossAmount ?? 0,
						inputChangeDiscountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
						inputChangeEscalation: formData?.changes?.[0]?.inputChangeEscalation ?? 0,
						inputChangeAdvance: formData?.changes?.[0]?.inputChangeAdvance,
						inputChangeEffectiveDate: formData?.changes?.[0]?.inputChangeEffectiveDate,
						inputChangeAdvanceAmounts: formData?.advanceAmounts,
						inputChangeGrossRents: formData?.grossRents,
						inputChangeDescription: formData?.changes?.[0]?.inputChangeDescription,
						upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
						initialDirectCost: formData?.initialDirectCost ?? 0,
						securityDeposit: formData?.securityDeposit ?? 0,
						cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					},
				],
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,

				lastModifiedDate: utcConverter(dayjs()),

				// grossRentListSet,
				// advanceAmountListSet,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
			};

			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to create?');

			updateFormState('submitPayload', createPagePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	useEffect(() => {
		if (displayLeaseData && displayLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = displayLeaseData.activationStatus;
		}
	}, [displayLeaseData]);

	const handleModalSubmit = async () => {
		await creationApi({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
		});
	};

	const selectTabHandler = (event, newValue) => {
		if (isCreatLoading) return;

		setSelectedTab(newValue);
		if (newValue === 1 && dataFullyLoaded && screen?.toLowerCase() !== 'create') {
			if (leaseScheduleRef.current?.queryV3Loader) {
				// leaseScheduleRef.current.queryV3Loader();
			}
		}
	};

	const showSpinner = screen?.toLowerCase() === 'view' ? isViewLoading && !initialCachedData : isCreatLoading;

	useEffect(() => {
		setIsHeaderPinned(sessionStorage.getItem(pinStorageKey) !== '0');
	}, [pinStorageKey]);

	useEffect(() => {
		if (selectedTab === 3 && dataFullyLoaded && screen?.toLowerCase() === 'view') {
			leaseDetailsShowAllRef.current?.loadData?.();
		}
	}, [dataFullyLoaded, screen, selectedTab]);

	const handleAutoPinToggle = () => {
		const nextState = !isHeaderPinned;
		setIsHeaderPinned(nextState);
		sessionStorage.setItem(pinStorageKey, nextState ? '1' : '0');
	};

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				// <>
				// 	<CardAccordion
				// 		title="Basic Information"
				// 		darkTitle={true}
				// 		sx={{
				// 			mb: 2,
				// 		}}
				// 		customHeaderSX={{
				// 			backgroundColor: '#e1f0fb !important',
				// 		}}
				// 		typographySX={{ fontSize: '16px !important' }}
				// 	>
				// 		<GeneralLeaseInformation screen={screen} vendorList={lynFormProperties?.vendorList} />
				// 	</CardAccordion>

				// 	<FinancialTermsConditions screen={screen} dataFullyLoaded={dataFullyLoaded} />
				// 	<CardAccordion
				// 		title="IFRS - 16 Specific Fields"
				// 		darkTitle={true}
				// 		sx={{
				// 			mb: 2,
				// 		}}
				// 		customHeaderSX={{
				// 			backgroundColor: '#e1f0fb !important',
				// 		}}
				// 		typographySX={{ fontSize: '16px !important' }}
				// 	>
				// 		<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} />
				// 	</CardAccordion>

				// 	<ActionButtons
				// 		screen={screen}
				// 		viewData={singleLeaseData}
				// 		selectHistory={setSelectHistory}
				// 		dataFullyLoaded={dataFullyLoaded}
				// 		ref={actionButtonsRef}
				// 		handleCreation={handleSubmit}
				// 		hasGlAssigned={hasGlAssigned}
				// 		hasCCAssigned={hasCCAssigned}
				// 	/>
				// </>

				<LeaseDetailBasicInformation
					screen={screen}
					viewData={singleLeaseData}
					dataFullyLoaded={dataFullyLoaded}
					vendorList={lynFormProperties?.vendorList}
					showFooterActions={false}
				/>
			) || <></>,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<LeaseRecordManagementV9ScheduleDetail
					screen={screen}
					ref={leaseScheduleRef}
					viewData={singleLeaseData}
					hasGlAssigned={hasGlAssigned}
					hasCCAssigned={hasCCAssigned}
					dataFullyLoaded={dataFullyLoaded}
					key={`lease-schedule-${singleLeaseData?.data?.leaseId || 'new'}-${tabInstanceId}`}
				/>
			),
		},
		{
			key: '4',
			label: 'Accounts Assignment',
			children: (
				<>
					<LeaseRecordManagementV9AccountAssignmentDetail
						form={form}
						screen={screen}
						leaseData={displayLeaseData}
						identityId={singleLeaseData?._id}
					/>
				</>
			),
		},

		{
			key: '5',
			label: 'General Ledgers',
			children: <LeaseManagementV9GeneralLedgersDetail ref={leaseDetailsShowAllRef} />,
		},
		{
			key: '6',
			label: 'History',
			children: (
				<>
					<LeaseRecordManagementV9HistoryTable />
				</>
			),
		},

		{
			key: '7',
			label: 'Agreements',
			children: <LeaseRecordManagementV9AgreementFiles />,
		},
	];

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<Box
					sx={{
						mb: 2,
						backgroundColor: isHeaderPinned ? 'background.main' : 'transparent',
						top: isHeaderPinned ? '60px' : 'auto',
						zIndex: isHeaderPinned ? 20 : 'auto',
						position: isHeaderPinned ? 'sticky' : 'static',
						px: { xs: 2, md: 2.5 },
						pt: { xs: 1.75, md: 2 },
						bgcolor: '#fff',
						border: '1px solid #e6ebf2',
						borderRadius: '10px',
						boxShadow: 'none',
					}}
				>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						alignItems={{ xs: 'flex-start', md: 'center' }}
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 1.25 }}
					>
						<Box>
							<Typography sx={{ color: '#1f2937', fontSize: '18px', fontWeight: 600, lineHeight: 1.2 }}>
								View Lease Record Management
							</Typography>
						</Box>

						<LeaseRecordHeaderActions
							form={form}
							state={state}
							viewData={displayLeaseData}
							isHeaderPinned={isHeaderPinned}
							onAutoPinToggle={handleAutoPinToggle}
							showActionButtons={selectedTab === 0}
						/>
					</Stack>

					<Box
						sx={{
							px: { xs: 0.25, md: 0.5 },
							borderTop: '1px solid #eef2f7',
						}}
					>
						<Tabs
							value={selectedTab}
							onChange={selectTabHandler}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							sx={{
								minHeight: '36px',
								'& .MuiTabs-indicator': {
									height: '2px',
									backgroundColor: '#2563eb',
									borderRadius: '999px',
								},
								'& .MuiTab-root': {
									minHeight: '36px',
									px: { xs: 1.25, md: 1.5 },
									mr: { xs: 0.5, md: 1 },
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: 500,
									color: '#667085',
								},
								'& .Mui-selected': {
									color: '#2563eb !important',
									fontWeight: 600,
								},
							}}
						>
							{tabItem?.map((item, index) => (
								<Tab key={item.key} label={item.label} />
							))}
						</Tabs>
					</Box>
				</Box>

				<Form
					form={form}
					layout="vertical"
					autoComplete="off"
					requiredMark={false}
					onFinish={handleSubmit}
					initialValues={initialFormValues}
				>
					<Box>{tabItem?.[selectedTab]?.children}</Box>
				</Form>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						modalData={formState?.modalData}
						handleSubmit={handleModalSubmit}
						setShowModal={setShowFormSubmitModal}
						showModal={formState?.showFormSubmitModal}
					/>
				)}

				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						selectItem={selectHistory}
						setSelectHistory={setSelectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default LeaseRecordManagementDetails;

const LeaseRecordHeaderActions = ({ form, state, viewData, isHeaderPinned, onAutoPinToggle, showActionButtons = true }) => {
	const navigate = useNavigate();
	const { creatorOrganizationId } = useSelector((currentState) => currentState.profile.profileData);
	const [getLeaseTableData, { data: leaseControlDataV3 }] = useGetQueryV3Mutation();
	const [draftSubmit, { isSuccess: isDraftSubmitSuccess }] = useIdentityBulkMutation();
	const [identityBulk, { isSuccess: isCaseSuccess, isError: isCaseError }] = useIdentityBulkMutation();
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});
	const [showClosedModal, setShowClosedModal] = useState(false);
	const [areAllRecordsRecorded, setAreAllRecordsRecorded] = useState(false);
	const [activeDeactiveSubmitData, setActiveDeactiveSubmitData] = useState(null);
	const [modalData, setModalData] = useState({
		head: state?.processName ?? '',
		label: 'Do you want to proceed?',
	});

	const updateLocalFormState = stateUpdater(setFormState);
	const setShowFormSubmitModal = (value) => {
		updateLocalFormState('showFormSubmitModal', value);
	};

	const isDraft = viewData?.activationStatus?.toLowerCase() === 'draft';
	const canModifyOrEdit = Boolean(viewData?._id) && viewData?.activationStatus !== 'W-I-P' && viewData?.activationStatus !== 'Closed';

	const handleSubmitClick = () => {
		const payloadData = [viewData?._id];

		updateLocalFormState('showFormSubmitModal', true);
		updateLocalFormState('modalData.head', 'Confirmation');
		updateLocalFormState('modalData.label', 'Do you want to submit this lease record?');
		updateLocalFormState('submitPayload', {
			suffixCode: '',
			_id: payloadData,
			operation: 'submit-draft-bulk',
			processCode: state?.processCode,
		});
	};

	const handleModalSubmit = async () => {
		await draftSubmit(formState?.submitPayload);
	};

	const handleSingleLeaseDeactivate = (status) => {
		setModalData({
			head: state?.processName ?? 'Lease Record',
			action: status,
			label: status === 'Closed' ? <p>Do you want to close this lease record?</p> : <p>Do you want to re-open this lease record?</p>,
		});
		setActiveDeactiveSubmitData([{ _id: state?._id }]);
		setShowClosedModal(true);
	};

	useEffect(() => {
		const queryV3Loader = async () => {
			const version = String(viewData?.data?.version);

			if (version && version !== 'null' && version !== 'undefined' && creatorOrganizationId && state?._id) {
				await getLeaseTableData({
					companyCode: creatorOrganizationId,
					name: 'leaseSchedulesOnVersion-v9',
					fieldValues: [creatorOrganizationId, state?._id, version],
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
				});
			}
		};

		if (viewData?._id) {
			queryV3Loader();
		}
	}, [creatorOrganizationId, getLeaseTableData, state?._id, viewData?._id, viewData?.data?.version]);

	useEffect(() => {
		if (Array.isArray(leaseControlDataV3?.results)) {
			setAreAllRecordsRecorded(leaseControlDataV3.results.every((item) => item?.processStatus === 'Recorded'));
		}
	}, [leaseControlDataV3]);

	const modalSubmitHandler = () => {
		if (!activeDeactiveSubmitData?.length) return;

		identityBulk({
			operation: 'activate-bulk',
			processCode: state?.processCode,
			activationStatus: modalData?.action,
			_id: activeDeactiveSubmitData.map((item) => item._id),
		});
		setShowClosedModal(false);
	};

	useEffect(() => {
		if (isDraftSubmitSuccess || isCaseSuccess) {
			if (isCaseSuccess) {
				toast.success('Success! Lease status updated successfully', toastConfig);
			}
			navigate(-1);
		}

		if (isCaseError) {
			toast.error('Failed! Status update not completed', toastConfig);
			setShowClosedModal(false);
		}
	}, [isDraftSubmitSuccess, isCaseSuccess, isCaseError, navigate]);

	const isLeaseLifeCycleOver = () => {
		const leaseEndDate = dayjs(form.getFieldValue('leaseEndDate'));
		return leaseEndDate.isValid() ? dayjs().isAfter(leaseEndDate) : false;
	};

	return (
		<>
			<Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
				<Tooltip title={isHeaderPinned ? 'Pinned' : 'Unpin'}>
					<MuiButton
						onClick={onAutoPinToggle}
						aria-label={isHeaderPinned ? 'Unpin header' : 'Pin header'}
						sx={{
							minWidth: '36px',
							width: '36px',
							height: '36px',
							borderRadius: '10px',
							color: isHeaderPinned ? '#1D4ED8' : '#374151',
							border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#E5E7EB'}`,
							backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
						}}
					>
						<AutoIcon />
					</MuiButton>
				</Tooltip>

				{showActionButtons &&
					(isDraft ? (
						<Button
							type="primary"
							onClick={handleSubmitClick}
							style={{
								height: '34px',
								paddingInline: '16px',
								borderRadius: '8px',
								fontWeight: 600,
								backgroundColor: '#2563eb',
								boxShadow: 'none',
							}}
						>
							Submit
						</Button>
					) : (
						<>
							{canModifyOrEdit && (
								<Button
									onClick={() => {
										if (navigate && state) {
											navigate('/static/lease-record-management-v9/modify-new-version', { state });
										}
									}}
									style={{
										height: '34px',
										display: 'flex',
										alignItems: 'center',
										paddingInline: '16px',
										borderRadius: '8px',
										fontWeight: 500,
										color: '#344054',
										borderColor: '#d0d5dd',
										backgroundColor: '#ffffff',
										boxShadow: 'none',
									}}
									icon={<EditIcon width="15px" height="15px" color="#344054" />}
								>
									Modify
								</Button>
							)}

							{canModifyOrEdit && (
								<Button
									onClick={() => {
										if (navigate && state) {
											navigate('/static/lease-record-management-v9/edit-new-version', { state });
										}
									}}
									style={{
										height: '34px',
										display: 'flex',
										alignItems: 'center',
										paddingInline: '16px',
										borderRadius: '8px',
										fontWeight: 500,
										color: '#344054',
										borderColor: '#d0d5dd',
										backgroundColor: '#ffffff',
										boxShadow: 'none',
									}}
									icon={<EditIcon width="15px" height="15px" color="#344054" />}
								>
									Edit
								</Button>
							)}
						</>
					))}

				{areAllRecordsRecorded &&
					isLeaseLifeCycleOver() &&
					(viewData?.activationStatus !== 'Closed' ? (
						<Button
							onClick={() => handleSingleLeaseDeactivate('Closed')}
							style={{
								height: '34px',
								display: 'flex',
								alignItems: 'center',
								paddingInline: '16px',
								borderRadius: '8px',
								fontWeight: 500,
							}}
							icon={<AssignmentTurnedInOutlinedIcon />}
						>
							Close
						</Button>
					) : (
						<Button
							onClick={() => handleSingleLeaseDeactivate('Reopened')}
							style={{
								height: '34px',
								display: 'flex',
								alignItems: 'center',
								paddingInline: '16px',
								borderRadius: '8px',
								fontWeight: 500,
							}}
							icon={<RestorePageOutlinedIcon />}
						>
							Reopen
						</Button>
					))}
			</Stack>

			{showClosedModal && (
				<ComptrollerConfirmModal
					modalData={modalData}
					showModal={showClosedModal}
					setShowModal={setShowClosedModal}
					handleSubmit={modalSubmitHandler}
				/>
			)}

			{formState?.showFormSubmitModal && (
				<ComptrollerConfirmModal
					handleSubmit={handleModalSubmit}
					modalData={formState?.modalData}
					setShowModal={setShowFormSubmitModal}
					showModal={formState?.showFormSubmitModal}
				/>
			)}
		</>
	);
};

export const useControlData = () => {
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const {
		data: getVendorList,
		isLoading: isVendorLoading,
		isError: isVendorError,
	} = useGetQueryV3DeltaQuery(
		{
			companyCode,
			fieldCodes: ['companyCode'],
			fieldValues: [companyCode],
			name: 'getVendorData',
		},
		{
			skip: !companyCode,
		}
	);

	const lynFormProperties = useMemo(
		() => ({
			vendorList:
				getVendorList?.results?.map((item) => ({
					label: item?.vendorName,
					value: item?._id,
					code: item?.vendorCode,
				})) ?? [],
		}),
		[getVendorList]
	);

	return {
		lynFormProperties,
		isLoading: isVendorLoading,
		isError: isVendorError,
	};
};